import { useState } from 'react'
import { PLANS, planOf } from '../../plans'
import { trialDaysLeft, type Tenant, type TenantActivity } from '../../services/superadmin'
import { ago, freshness } from '../../utils/ago'
import { XIcon } from '../icons'
import { ColumnFilter } from './ColumnFilter'
import './FeatureTable.css'

/** Bitiş sütunu: deneme sürüyor, 7 gün içinde bitiyor, bitti, süresiz. */
type End = 'yakin' | 'deneme' | 'bitti' | 'suresiz'
const END_NAME: Record<End, string> = {
  yakin: '7 günde biter',
  deneme: 'Denemede',
  bitti: 'Bitti',
  suresiz: 'Süresiz',
}

function endOf(t: Tenant): End {
  const left = trialDaysLeft(t)
  if (left === null) return 'suresiz'
  if (left <= 0) return 'bitti'
  return left <= 7 ? 'yakin' : 'deneme'
}

/** Başlangıç sütunu: kurumun açılışı. */
type Start = 'hafta' | 'ay' | 'eski'
const START_NAME: Record<Start, string> = {
  hafta: 'Son 7 gün',
  ay: 'Son 30 gün',
  eski: '30 günden eski',
}
function startOf(t: Tenant): Start {
  const days = (Date.now() - Date.parse(t.created_at)) / 86_400_000
  return days <= 7 ? 'hafta' : days <= 30 ? 'ay' : 'eski'
}

/** Zaman sütunlarının filtresi: son 24 saat, son 7 gün, daha eski, hiç. */
type When = 'fresh' | 'warm' | 'cold' | 'never'
const WHEN_NAME: Record<When, string> = {
  fresh: 'Son 24 saat',
  warm: 'Son 7 gün',
  cold: '7 günden eski',
  never: 'Hiç',
}
const whenOf = (iso: string | null | undefined): When => (iso ? freshness(iso) : 'never')

const fold = (s: string) => s.toLocaleLowerCase('tr')

/** Ad sözlüğünden filtre seçenekleri. */
const opts = <K extends string>(names: Record<K, string>) =>
  (Object.keys(names) as K[]).map((k) => ({ value: k, label: names[k] }))

const dateText = (iso: string) =>
  new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
/** Açılıştan bu yana: "bugün", "12 gün önce", "2 ay önce". */
function since(iso: string) {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
  if (days < 1) return 'bugün'
  if (days < 60) return `${days} gün önce`
  return `${Math.floor(days / 30)} ay önce`
}
const clockText = (iso: string) =>
  new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/**
 * Müşteriler tablosu (31 Eki 2026, kullanıcı): her kurum bir satır; başlangıç,
 * bitiş, paket, son görülme, son işlem, aktif kullanıcı. Her sütunun
 * başlığında filtresi var. Satıra basınca kurumun ayarları sağdaki çekmecede açılır.
 */
export function TenantTable({
  tenants,
  activity = {},
  selectedId,
  onSelect,
}: {
  tenants: Tenant[]
  /** Son görülme ve kullanıcılar (sistem yöneticisi fonksiyonu); yüklenemezse boş. */
  activity?: Record<string, TenantActivity>
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const [query, setQuery] = useState('')
  const [plan, setPlan] = useState('')
  const [start, setStart] = useState<Start | ''>('')
  const [end, setEnd] = useState<End | ''>('')
  const [seen, setSeen] = useState<When | ''>('')
  const [action, setAction] = useState<When | ''>('')
  const [users, setUsers] = useState<'' | 'var' | 'yok'>('')

  if (tenants.length === 0) {
    return <p className="superadmin__empty">Henüz hiç işletme açılmamış.</p>
  }

  const q = fold(query.trim())
  const rows = tenants.filter((t) => {
    const a = activity[t.id]
    if (q && ![t.display_name, t.id, t.contact_phone ?? ''].some((s) => fold(s).includes(q))) return false
    if (plan && (plan === 'yok' ? t.plan !== null : t.plan !== plan)) return false
    if (start && startOf(t) !== start) return false
    if (end && endOf(t) !== end) return false
    if (seen && whenOf(a?.seen) !== seen) return false
    if (action && whenOf(a?.lastAction) !== action) return false
    if (users === 'var' && !((a?.liveUsers ?? 0) > 0)) return false
    if (users === 'yok' && (a?.liveUsers ?? 0) > 0) return false
    return true
  })
  const filtered = [query, plan, start, end, seen, action, users].some((v) => v !== '')

  return (
    <div className="ftable ftable--tenants">
      <div className="ftable__wrap">
        <table>
          <thead>
            {/* Filtreler başlıkta (1 Kas 2026, kullanıcı): ikinci satır yok. */}
            <tr>
              <th>
                <ColumnFilter label="İşletme" query={query} onQuery={setQuery} placeholder="Ad, kod, telefon" />
              </th>
              <th className="ttable__col-plan">
                <ColumnFilter
                  label="Paket"
                  value={plan}
                  onChange={setPlan}
                  options={[...PLANS.map((p) => ({ value: p.id, label: p.name })), { value: 'yok', label: 'Paketsiz' }]}
                />
              </th>
              <th className="ttable__col-date">
                <ColumnFilter label="Başlangıç" value={start} onChange={(v) => setStart(v as Start | '')} options={opts(START_NAME)} />
              </th>
              <th className="ttable__col-end">
                <ColumnFilter label="Bitiş" value={end} onChange={(v) => setEnd(v as End | '')} options={opts(END_NAME)} />
              </th>
              <th className="ttable__col-when">
                <ColumnFilter label="Son görülme" value={seen} onChange={(v) => setSeen(v as When | '')} options={opts(WHEN_NAME)} />
              </th>
              <th className="ttable__col-when">
                <ColumnFilter label="Son işlem" value={action} onChange={(v) => setAction(v as When | '')} options={opts(WHEN_NAME)} />
              </th>
              <th className="ttable__col-users">
                <ColumnFilter
                  label="Kayıtlı kullanıcı"
                  value={users}
                  onChange={(v) => setUsers(v as '' | 'var' | 'yok')}
                  options={[
                    { value: 'var', label: 'Canlı var' },
                    { value: 'yok', label: 'Canlı yok' },
                  ]}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const a = activity[t.id]
              const p = planOf(t.plan)
              const e = endOf(t)
              const left = trialDaysLeft(t)
              return (
                <tr
                  key={t.id}
                  className={`ttable__row ${t.id === selectedId ? 'is-selected' : ''}`}
                  onClick={() => onSelect(t.id)}
                >
                  <td>
                    <button type="button" className="ttable__name" onClick={() => onSelect(t.id)}>
                      <strong>{t.display_name}</strong>
                      <span>
                        {t.id}
                        {t.deletion_requested_at && <em className="ttable__del">Silinecek</em>}
                      </span>
                    </button>
                  </td>
                  <td>
                    {p ? (
                      <span className={`ftable__plan is-${p.id}`}>
                        {p.name}
                        {t.plan_period === 'yillik' ? ', yıllık' : ''}
                      </span>
                    ) : (
                      <span className="ttable__muted">Paketsiz</span>
                    )}
                  </td>
                  <td>
                    <span className="ttable__two">
                      <b>{dateText(t.created_at)}</b>
                      <small>{since(t.created_at)}</small>
                    </span>
                  </td>
                  <td>
                    {t.trial_ends_at ? (
                      <span className="ttable__two">
                        <b>{dateText(t.trial_ends_at)}</b>
                        <small className={`ttable__left is-${e}`}>
                          {e === 'bitti' ? 'Deneme bitti' : `Deneme: ${left} gün kaldı`}
                        </small>
                      </span>
                    ) : (
                      <span className="ttable__muted">Süresiz</span>
                    )}
                  </td>
                  <td>
                    <span
                      className="ttable__two"
                      title={a ? `Şifreyle son giriş: ${ago(a.login)}` : undefined}
                    >
                      <b className="ttable__when">
                        <i className={`tenant-list__dot is-${freshness(a?.seen)}`} />
                        {ago(a?.seen)}
                      </b>
                      {a?.seen && <small>{clockText(a.seen)}</small>}
                    </span>
                  </td>
                  <td>
                    <span className="ttable__two">
                      <b>{ago(a?.lastAction)}</b>
                      {a?.lastAction && <small>{clockText(a.lastAction)}</small>}
                    </span>
                  </td>
                  <td>
                    {a && a.users !== null ? (
                      <span
                        className="ttable__two"
                        title="Kayıtlı: patron, kasiyer ve garson hesapları (mutfak ve kiosk ekranı sayılmaz). Canlı: şu an uygulamayı kullanan (son 1 saat)."
                      >
                        <b>{a.users} kullanıcı</b>
                        <small className={`ttable__live ${a.liveUsers ? 'is-on' : ''}`}>
                          <i className={`tenant-list__dot ${a.liveUsers ? 'is-fresh' : ''}`} />
                          {a.liveUsers} canlı
                        </small>
                      </span>
                    ) : (
                      <span className="ttable__muted">?</span>
                    )}
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="ftable__empty">
                  Bu filtrelere uyan işletme yok.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered && (
        <div className="ftable__foot">
          <span>
            {rows.length}/{tenants.length} işletme gösteriliyor
          </span>
          <button
            type="button"
            className="ftable__clear"
            onClick={() => {
              setQuery('')
              setPlan('')
              setStart('')
              setEnd('')
              setSeen('')
              setAction('')
              setUsers('')
            }}
          >
            <XIcon />
            Filtreleri temizle
          </button>
        </div>
      )}
    </div>
  )
}
